// ── 매출 관리 통합 어셈블리 ───────────────────────────────────────────────────
// 파인그룹 / DOT / LNJ 등 모든 회사에서 companyCode prop 하나로 분기
// 2026-08 디자인 통일: 직원 관리 표준(designStandard.js) 헤더 + 알약 탭
import React, { useEffect, useState } from 'react';
import { useToast }  from '../../../components/Toast';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdTabGroup, stdTabBtn,
} from '../adminlayout/designStandard';

import { useRevenue }       from './useRevenue';
import { useRevenueUpload } from './useRevenueUpload';

import { TabPayments }  from './TabPayments';
import { TabInvoices }  from './TabInvoices';
import { TabPhone }     from './TabPhone';
import { TabOthers }    from './TabOthers';
import { TabCards }     from './TabCards';
import { LinkPopup }    from './LinkPopup';

import {
  PaymentModal, TaxInvoiceModal, CardPhoneModal, OtherModal,
} from './EntryModals';
import { AuditLogModal, PaymentMigrationModal } from './AuditLogModal';

/**
 * 매출 관리 통합 페이지
 * @param {string|null} companyCode — null: 파인그룹, 'DOT': DOT, 'LNJ': LNJ, 기타 외부사
 */
export default function RevenueManagement({ companyCode = null }) {
  const { showToast } = useToast();

  // 표준 헤더 반응형 분기 (직원 관리와 동일 브레이크포인트)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 통합 훅 — companyCode에 따라 apiBase·tblPrefix·권한 코드가 자동 분기
  const state = useRevenue({ companyCode, showToast });

  // 파일 업로드 핸들러 훅 — apiBase는 state에서 전달
  const { handleInvoiceFileSelect, handleFileUpload } = useRevenueUpload(
    state, showToast, state.apiBase
  );

  const {
    tblPrefix,
    canUseSalesManagement, canUseSalesCardManagement, permReady,
    tab, setTab,
    salesUsers, taxInvoices,
    payments, invoices, phoneApps, others, cardApps, cardBatches, taxBatches,
    filterStart, setFilterStart, filterEnd, setFilterEnd,
    modal, setModal,
    linkPopup, setLinkPopup,
    migrationQueue,
    fileRef, invoiceFileRef, uploading, invoiceUploading,
    terminalMap,
    loadPayments, loadTaxInvoices, loadInvoices,
    loadPhoneApps, loadOthers, loadCardApps, loadCardBatches,
    handleDelete,
    handleCardBatchDelete, handleTaxBatchDelete,
    handleCardBatchDownload, handleTaxBatchDownload,
    handleLinkClick, handleLinkInvoice,
    advanceMigrationQueue, handleMigratePayments,
    handleModalSave,
  } = state;

  // 탭 가시성 계산
  const canSeeCards = canUseSalesManagement || canUseSalesCardManagement;
  const TABS = [
    canUseSalesManagement && { key: 'payments', label: '입금' },
    canUseSalesManagement && { key: 'invoices', label: '세금계산서' },
    canSeeCards           && { key: 'cards',    label: '카드승인' },
    canUseSalesManagement && { key: 'phone',    label: '카드전화승인' },
    canUseSalesManagement && { key: 'others',   label: '기타' },
  ].filter(Boolean);

// 제목에 회사 접미사를 붙이지 않는다 (2026-09-14) — 어느 회사인지는 상단 작업대상 선택창이 알려준다.
//   2026-09-10 에 5곳을 정리했는데 여기가 빠져 있었다. 회사코드(SUNCREAM 등)가 화면에 그대로 새어 나왔다.
  const pageTitle = '매출 관리';

  return (
    <div>
      {/* ── 페이지 헤더: 제목/부제(좌) + 탭 알약 토글(우) — 직원 관리 표준 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>{pageTitle}</h2>
          <div style={stdPageSubtitle}>매출 입력과 집계를 관리합니다.</div>
        </div>
        <div style={{ ...stdTabGroup, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button key={t.key} style={stdTabBtn(tab === t.key)} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 이 회사 열쇠가 없을 때(주소를 직접 연 경우) — 왼쪽 메뉴에는 애초에 안 나온다(2026-11-05) */}
      {permReady && !canSeeCards && (
        <div style={{ padding: 24, color: 'var(--text-muted)' }}>이 회사의 매출 관리 권한이 없습니다.</div>
      )}

      {/* 탭 콘텐츠 */}
      <div>
        {tab === 'payments' && canUseSalesManagement && (
          <TabPayments
            payments={payments}
            filterStart={filterStart} setFilterStart={setFilterStart}
            filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
            loadPayments={loadPayments} loadTaxInvoices={loadTaxInvoices}
            setModal={setModal}
            handleLinkClick={handleLinkClick} linkPopup={linkPopup}
            handleDelete={handleDelete}
            auditTableName={`${tblPrefix}_payments`}
          />
        )}
        {tab === 'invoices' && canUseSalesManagement && (
          <TabInvoices
            invoices={invoices} taxBatches={taxBatches}
            filterStart={filterStart} setFilterStart={setFilterStart}
            filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
            loadInvoices={loadInvoices} loadTaxInvoices={loadTaxInvoices}
            invoiceFileRef={invoiceFileRef}
            invoiceUploading={invoiceUploading}
            handleInvoiceFileSelect={handleInvoiceFileSelect}
            setModal={setModal} handleDelete={handleDelete} salesUsers={salesUsers}
            auditTableName={`${tblPrefix}_tax_invoices`}
            apiBase={state.apiBase}
            onDeleteBatch={handleTaxBatchDelete}
            onDownloadBatch={handleTaxBatchDownload}
          />
        )}
        {tab === 'phone' && canUseSalesManagement && (
          <TabPhone
            phoneApps={phoneApps}
            filterStart={filterStart} setFilterStart={setFilterStart}
            filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
            loadPhoneApps={loadPhoneApps}
            setModal={setModal} handleDelete={handleDelete}
            auditTableName={`${tblPrefix}_card_phone_approvals`}
          />
        )}
        {tab === 'others' && canUseSalesManagement && (
          <TabOthers
            others={others}
            filterStart={filterStart} setFilterStart={setFilterStart}
            filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
            loadOthers={loadOthers}
            setModal={setModal} handleDelete={handleDelete}
            auditTableName={`${tblPrefix}_others`}
          />
        )}
        {tab === 'cards' && canSeeCards && (
          <TabCards
            cardApps={cardApps} cardBatches={cardBatches} terminalMap={terminalMap}
            filterStart={filterStart} setFilterStart={setFilterStart}
            filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
            loadCardApps={loadCardApps} loadCardBatches={loadCardBatches}
            fileRef={fileRef} uploading={uploading} handleFileUpload={handleFileUpload}
            setModal={setModal}
            auditTableName={`${tblPrefix}_card_approvals`}
            onDeleteBatch={handleCardBatchDelete}
            onDownloadBatch={handleCardBatchDownload}
          />
        )}
      </div>

      {/* 입력/수정 모달 */}
      {modal?.type === 'payment' && (
        <PaymentModal
          initial={modal.data} taxInvoices={taxInvoices} salesUsers={salesUsers}
          onSave={handleModalSave} onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'invoice' && (
        <TaxInvoiceModal
          initial={modal.data} salesUsers={salesUsers}
          onSave={handleModalSave} onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'phone' && (
        <CardPhoneModal
          initial={modal.data} salesUsers={salesUsers}
          onSave={handleModalSave} onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'other' && (
        <OtherModal
          initial={modal.data} salesUsers={salesUsers}
          onSave={handleModalSave} onClose={() => setModal(null)}
        />
      )}

      {/* 변경이력 모달 */}
      {modal?.type === 'auditlog' && (
        <AuditLogModal
          title={modal.title} tableName={modal.tableName} apiBase={state.apiBase}
          onClose={() => setModal(null)}
        />
      )}

      {/* 수정 세금계산서 입금 이관 모달 — 큐 순차 처리 */}
      {migrationQueue.length > 0 && (
        <PaymentMigrationModal
          modifiedInvoice={migrationQueue[0]}
          candidates={taxInvoices.filter((ti) =>
            ['청구', '영수'].includes(ti.issue_type) &&
            ti.customer_name === migrationQueue[0].customerName
          )}
          onConfirm={handleMigratePayments}
          onSkip={advanceMigrationQueue}
        />
      )}

      {/* 세금계산서 연결 팝업 — "미연결" 버튼 우측 position:fixed */}
      <LinkPopup
        linkPopup={linkPopup}
        taxInvoices={taxInvoices}
        onSelect={handleLinkInvoice}
        onClose={() => setLinkPopup(null)}
      />
    </div>
  );
}
